// src/modules/hrm/onLeave/OnLeaveListPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSun, faEye } from "@fortawesome/free-solid-svg-icons";
import { Building2, Calendar, Sparkles } from "lucide-react";
import Filter from "./OnLeaveFilter";
import Pagination from "../../../components/common/Pagination";
import PendingOnLeaveList from "./PendingOnLeaveList";
import CreateDrawer from "./OnLeaveCreateModalNew";
import OnLeaveDetailModal from "./OnLeaveDetailModal"; // 👁 popup chi tiết nghỉ phép
import {
  getAllOnLeaves,
  getPendingOnLeaves,
} from "../../../api/hrm/on_leaves/onLeave";

import { showSuccessToast, showErrorToast } from "../../../components/ConfirmDialog";
import { formatDateVN } from "../../../utils/formatUtils";
import { downloadHrmListingExcel } from "../hrmListingExportExcel";
import { startOnLeaveListTour } from "./onLeaveListTour";

function formatApprovedAtExport(v) {
  if (v == null || v === "") return "";
  const s = String(v);
  if (/^\d{1,2}\/\d{1,2}\/\d{4}/.test(s.trim())) return s;
  return formatDateVN(v);
}

const OnLeaveListPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [filters, setFilters] = useState({});
  const [data, setData] = useState([]);
  const [pendingOnLeaves, setPendingOnLeaves] = useState([]);
  const [showDrawer, setShowDrawer] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedLeave, setSelectedLeave] = useState(null); // 👁

  const notificationTarget = useMemo(() => {
    const target = location.state?.notificationTarget;
    if (target?.moduleCode !== "hrm.on_leave") return null;
    return target;
  }, [location.state]);

  // Chỉ fetch 1 lần khi khởi tạo page (tránh trùng do OnLeaveFilter mount)
  const didInitialFetchRef = useRef(false);

  // =============================
  // 🔹 Load danh sách nghỉ phép (bảng)
  // =============================
  const fetchData = async (
    page = currentPage,
    size = pageSize,
    filtersOverride = filters
  ) => {
    try {
      const res = await getAllOnLeaves({ ...filtersOverride, page, size });
      if (res?.records) {
        setData(res.records);
        setTotalRecords(res.total || res.records.length);
      } else {
        setData(res || []);
        setTotalRecords(res?.length || 0);
      }
    } catch (err) {
      console.error("❌ Lỗi khi tải danh sách nghỉ phép:", err);
    }
  };

  // =============================
  // 🔹 Load danh sách chờ duyệt (card)
  // =============================
  const fetchPendingOnLeaves = async (filtersOverride = filters) => {
    try {
      const res = await getPendingOnLeaves(filtersOverride);
      setPendingOnLeaves(res || []);
    } catch (err) {
      console.error("❌ Lỗi khi tải danh sách nghỉ phép chờ duyệt:", err);
    }
  };

  //--------------------------------------------------------------------
  // 📌 EXPORT EXCEL – cùng kiểu định dạng với Chấm công
  //--------------------------------------------------------------------
  const parseMonthKey = (ym) => {
    if (!ym || typeof ym !== "string" || !/^\d{4}-\d{2}$/.test(ym)) return null;
    const [yStr, mStr] = ym.split("-");
    const year = parseInt(yStr, 10);
    const month = parseInt(mStr, 10);
    if (Number.isNaN(year) || Number.isNaN(month) || month < 1 || month > 12)
      return null;

    const pad = (n) => String(n).padStart(2, "0");
    const lastDay = new Date(year, month, 0).getDate();
    const monthPadded = pad(month);

    return {
      year,
      month,
      monthPadded,
      from_date: `${year}-${monthPadded}-01`,
      to_date: `${year}-${monthPadded}-${pad(lastDay)}`,
    };
  };

  const handleDownloadCsv = async ({ branch_id, month, branch_label } = {}) => {
    try {
      const parsedMonth = month ? parseMonthKey(month) : null;
      const from_date = parsedMonth ? parsedMonth.from_date : filters.from_date;
      const to_date = parsedMonth ? parsedMonth.to_date : filters.to_date;

      if (!from_date || !to_date) {
        showErrorToast("Chọn khoảng thời gian (từ ngày – đến ngày).");
        return;
      }

      const query = {
        ...filters,
        page: 1,
        size: 10000,
        from_date,
        to_date,
      };
      if (branch_id) query.branch_id = branch_id;

      const res = await getAllOnLeaves(query);
      const allData = res?.records || res || [];

      if (allData.length === 0) {
        showErrorToast("Không có dữ liệu để xuất.");
        return;
      }

      const header = [
        "STT",
        "Chi nhánh",
        "Bộ phận",
        "Nhân viên",
        "Loại nghỉ phép",
        "Từ ngày",
        "Đến ngày",
        "Số ngày",
        "Lý do",
        "Trạng thái",
        "Người duyệt cuối",
        "Ngày duyệt",
      ];

      const rows = allData.map((r, i) => [
        i + 1,
        r.branch_name ?? "",
        r.department_name ?? "",
        r.employee_name ?? "",
        r.leave_type ?? "",
        formatDateVN(r.from_date),
        formatDateVN(r.to_date),
        r.total_days ?? "",
        r.reason ?? "",
        r.status ?? "",
        r.approver_name ?? "",
        formatApprovedAtExport(r.approved_at),
      ]);
      const aoa = [header, ...rows];

      const branchText =
        branch_label?.trim() ||
        (branch_id ? "Chi nhánh" : "Tất cả chi nhánh");
      const branchTag = branch_id ? String(branch_id).slice(0, 8) : "tat-ca";

      const title = parsedMonth
        ? `Dữ liệu nghỉ phép tháng ${parsedMonth.monthPadded} năm ${parsedMonth.year} của ${branchText}`
        : `Dữ liệu nghỉ phép từ ${formatDateVN(from_date)} đến ${formatDateVN(to_date)}`;

      const filename = parsedMonth
        ? `nghi-phep_${month}_${branchTag}.xlsx`
        : `nghi-phep_${from_date}_${to_date}_${branchTag}.xlsx`;

      await downloadHrmListingExcel(aoa, {
        title,
        sheetName: "NghiPhep",
        filename,
        columnWidths: [6, 20, 18, 22, 16, 16, 16, 10, 36, 14, 18, 18],
      });

      showSuccessToast("Đã tải file Excel.");
    } catch (err) {
      console.error("❌ Lỗi export Excel:", err);
      showErrorToast("Không thể xuất Excel.");
    }
  };

  // Dùng khi duyệt / không duyệt / tạo mới → load lại theo filter hiện tại
  const refreshOnLeaves = async () => {
    await Promise.all([
      fetchPendingOnLeaves(filters),
      fetchData(currentPage, pageSize, filters),
    ]);
  };

  const handleApprovedOnLeave = async () => await refreshOnLeaves();
  const handleRejectOnLeave = async () => await refreshOnLeaves();

  // Lần đầu vào trang hoặc đổi page/pageSize thì load theo filters hiện tại
  useEffect(() => {
    window.onReloadLeaveList = () => refreshOnLeaves();

    // Chỉ gọi API khi filter đã được khởi tạo đầy đủ từ OnLeaveFilter
    if (!filters?.from_date || !filters?.to_date) return;
    if (didInitialFetchRef.current) return;

    didInitialFetchRef.current = true;
    fetchData(currentPage, pageSize, filters);
    fetchPendingOnLeaves(filters);
  }, [currentPage, pageSize]);

  // Khi filter thay đổi từ component Filter
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
    // ✅ dùng chính newFilters, không dùng state filters (tránh bị trễ)
    fetchData(1, pageSize, newFilters);
    fetchPendingOnLeaves(newFilters);

    // đánh dấu đã fetch init (tránh useEffect chạy thêm)
    didInitialFetchRef.current = true;
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchData(page, pageSize, filters);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
    fetchData(1, size, filters);
  };

  const storedUser = localStorage.getItem("user");
  const user = storedUser ? JSON.parse(storedUser) : null;

  // =============================
  // 🔹 Giao diện hiển thị
  // =============================
  return (
    <div className="p-4">
      {/* HEADER */}
      <div className="flex items-center justify-between mb-8" data-tour="hrm-onleave-header">
        <div className="flex items-center gap-4">
          <div className="bg-red-700 p-2.5 rounded-2xl text-white shadow-lg shadow-red-200">
            <FontAwesomeIcon
              icon={faSun}
              className="text-xl w-6 h-6 flex items-center justify-center"
            />
          </div>

          <div>
            <h1 className="text-xl font-black text-gray-800 tracking-tight uppercase">
              Quản lý <span className="text-red-700">nghỉ phép</span>
            </h1>

            <div className="flex items-center gap-2 mt-0.5">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
              <p className="text-[11px] text-gray-500 font-bold uppercase tracking-wider text-nowrap">
                <span className="text-red-600">{totalRecords}</span>{" "} phiếu nghỉ phép đã đăng ký
              </p>
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => window.setTimeout(() => startOnLeaveListTour(), 80)}
          title="Hướng dẫn nhanh"
          aria-label="Hướng dẫn nhanh"
          className="group relative inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-red-200 bg-white text-red-700 shadow-sm transition-all hover:bg-red-50 hover:border-red-300"
        >
          <Sparkles className="h-5 w-5" strokeWidth={2.2} />
          <span className="pointer-events-none absolute bottom-full right-0 mb-2 rounded-lg border border-red-100 bg-white px-2.5 py-1 text-[11px] font-bold text-red-700 whitespace-nowrap opacity-0 translate-y-1 shadow-md transition-all group-hover:opacity-100 group-hover:translate-y-0">
            Hướng dẫn nhanh
          </span>
        </button>
      </div>

      <div data-tour="hrm-onleave-filter">
        <Filter
          onNewClick={() => setShowDrawer(true)}
          onFilterChange={handleFilterChange}
          onRefresh={refreshOnLeaves}
          onDownloadCsv={handleDownloadCsv}
        />
      </div>

      {/* Danh sách nghỉ phép chờ duyệt (Card-list) */}
      <div data-tour="hrm-onleave-pending">
        <PendingOnLeaveList
          data={pendingOnLeaves}
          currentUserId={user?.id}
          currentUserRole={user?.role}
          autoOpenId={notificationTarget?.referenceId}
          onAutoOpened={() => navigate(location.pathname, { replace: true, state: null })}
          onApproved={handleApprovedOnLeave}
          onReject={handleRejectOnLeave}
        />
      </div>

      {/* Bộ lọc */}
      <h2 className="font-semibold text-base text-gray-700 mb-3 ml-2 mt-10">
        Bảng danh sách phiếu nghỉ phép ({totalRecords} phiếu)
      </h2>

      {/* Bảng dữ liệu */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-tour="hrm-onleave-table">
        <div className="overflow-x-auto">
          <table className="w-full table-auto border-collapse">
            <thead>
              <tr className="bg-gray-50/50 border-b border-gray-100 text-left">
                <th className="p-4 w-[50px] text-center text-xs font-bold text-gray-400 uppercase tracking-wider">
                  #
                </th>
                <th className="p-4 min-w-[220px] text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Nhân viên & Bộ phận
                </th>
                <th className="p-4 min-w-[150px] text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Chi nhánh
                </th>
                <th className="p-4 min-w-[150px] text-center text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Thời gian nghỉ
                </th>
                <th className="p-4 min-w-[120px] text-center text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Số ngày
                </th>
                <th className="p-4 min-w-[150px] text-center text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Loại phép
                </th>
                <th className="p-4 min-w-[200px] text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Lý do
                </th>
                <th className="p-4 min-w-[140px] text-center text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Trạng thái
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-50">
              {data.length === 0 ? (
                <tr>
                  <td colSpan={9} className="p-20 text-center">
                    <div className="flex flex-col items-center">
                      <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mb-4">
                        <Calendar size={32} className="text-gray-200" />
                      </div>
                      <p className="text-gray-400 font-medium text-sm">
                        Hiện chưa có dữ liệu nghỉ phép nào.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                data.map((item, index) => {
                  // Xác định màu cho Loại phép
                  const leaveTypeStyle =
                    item.leave_type === "Phép năm"
                      ? "bg-blue-50 text-blue-700 border-blue-100"
                      : item.leave_type === "Bảo hiểm xã hội"
                      ? "bg-purple-50 text-purple-700 border-purple-100"
                      : "bg-pink-50 text-pink-700 border-pink-100";

                  // Xác định màu cho Trạng thái
                  const statusStyle =
                    item.status === "Đã duyệt"
                      ? "bg-green-50 text-green-700 border-green-100"
                      : item.status === "Không duyệt"
                      ? "bg-red-50 text-red-700 border-red-100"
                      : "bg-orange-50 text-orange-700 border-orange-100";

                  return (
                    <tr
                      key={item.id}
                      onClick={() => setSelectedLeave(item)} // 🟢 Click row mở detail
                      className="hover:bg-red-50/30 transition-colors group cursor-pointer"
                    >
                      {/* 1. STT */}
                      <td className="p-4 text-sm text-gray-400 text-center font-medium italic">
                        {(currentPage - 1) * pageSize + index + 1}
                      </td>

                      {/* 2. NHÂN VIÊN */}
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-red-100 text-red-600 flex items-center justify-center font-bold text-sm shrink-0 border border-white">
                            {item.employee_name?.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-bold text-gray-900 group-hover:text-red-600 transition-colors text-sm capitalize">
                              {item.employee_name?.toLowerCase()}
                            </div>
                            <div className="text-[10px] text-gray-500 font-medium italic leading-none mt-0.5">
                              {item.department_name}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* 3. CHI NHÁNH */}
                      <td className="p-4 text-sm font-medium text-gray-600">
                        <div className="flex items-center gap-1.5">
                          <Building2 size={14} className="text-gray-400" />
                          {item.branch_name}
                        </div>
                      </td>

                      {/* 4. THỜI GIAN NGHỈ */}
                      <td className="p-4 text-center">
                        <div className="flex flex-col items-center">
                          <div className="text-[11px] font-bold text-gray-700">
                            {formatDateVN(item.from_date)}
                          </div>
                          <div className="h-2 w-[1px] bg-gray-200 my-0.5"></div>
                          <div className="text-[11px] font-bold text-gray-700">
                            {formatDateVN(item.to_date)}
                          </div>
                        </div>
                      </td>

                      {/* 5. SỐ NGÀY */}
                      <td className="p-4 text-center">
                        <span
                          className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold text-xs border shadow-sm
                    ${
                      Number(item.total_days) < 2
                        ? "bg-green-50 text-green-700 border-green-100"
                        : Number(item.total_days) > 3
                        ? "bg-red-50 text-red-700 border-red-100"
                        : "bg-amber-50 text-amber-700 border-amber-100"
                    }
                  `}
                        >
                          {Number(item.total_days)}
                        </span>
                      </td>

                      {/* 6. LOẠI PHÉP */}
                      <td className="p-4 text-center">
                        <span
                          className={`px-2.5 py-1 text-[10px] font-bold rounded-full border uppercase tracking-wider ${leaveTypeStyle}`}
                        >
                          {item.leave_type || "-"}
                        </span>
                      </td>

                      {/* 7. LÝ DO */}
                      <td className="p-4">
                        <p
                          className="text-xs text-gray-600 italic line-clamp-2 leading-relaxed"
                          title={item.reason}
                        >
                          {item.reason || "Không có lý do"}
                        </p>
                      </td>

                      {/* 8. TRẠNG THÁI */}
                      <td className="p-4 text-center">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide border ${statusStyle}`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                              item.status === "Chờ duyệt"
                                ? "bg-orange-500 animate-pulse"
                                : "bg-current opacity-60"
                            }`}
                          />
                          {item.status}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Phân trang */}
      <Pagination
        totalRecords={totalRecords}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      {/* Drawer tạo mới */}
      <CreateDrawer
        isOpen={showDrawer}
        onClose={() => setShowDrawer(false)}
        onSaved={() => {
          refreshOnLeaves();
          setShowDrawer(false);
        }}
      />

      {/* Popup chi tiết nghỉ phép 👁 */}
      {selectedLeave && (
        <OnLeaveDetailModal
          leave={selectedLeave}
          onClose={() => setSelectedLeave(null)}
        />
      )}
    </div>
  );
};

export default OnLeaveListPage;
